/**
 * OtterIntegrationSettings
 *
 * Settings page for Otter.ai email forwarding-based transcript ingestion.
 * Users forward Otter meeting summary emails to our ingest address via
 * Gmail/Outlook forwarding rules.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Check,
  Clock,
  Copy,
  Loader2,
  Mail,
  Mic,
  Plus,
  Save,
  X,
} from 'lucide-react';
import { useOtterIntegration } from '@/hooks/useOtterIntegration';
import { toast } from 'sonner';

export const OtterIntegrationSettings: React.FC = () => {
  const {
    status,
    loadingStatus,
    saving,
    unassignedCount,
    configureIngest,
  } = useOtterIntegration();

  const [domains, setDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (status.configured) {
      setDomains(status.allowedSenderDomains || []);
      setEnabled(status.enabled !== false);
    }
  }, [status.configured, status.allowedSenderDomains, status.enabled]);

  const handleCopyEmail = async () => {
    if (!status.ingestEmail) return;
    try {
      await navigator.clipboard.writeText(status.ingestEmail);
    } catch {
      const input = document.createElement('input');
      input.value = status.ingestEmail;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddDomain = () => {
    const domain = newDomain.toLowerCase().trim();
    if (!domain) return;
    if (domains.includes(domain)) {
      toast.info('Domain already added');
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(domain)) {
      toast.error('Invalid domain format. Use format like "company.com"');
      return;
    }
    setDomains([...domains, domain]);
    setNewDomain('');
  };

  const handleRemoveDomain = (domain: string) => {
    setDomains(domains.filter((d) => d !== domain));
  };

  const handleSave = async () => {
    await configureIngest(domains, enabled);
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Mic className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Otter.ai Meeting Transcripts</p>
              <p className="text-xs text-muted-foreground mt-1">
                Automatically import meeting transcripts from Otter.ai by forwarding Otter's
                meeting summary emails to your ingest address. AI classifies each transcript to the correct project.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mic className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">
                {status.configured ? 'Otter.ai Configuration' : 'Set Up Otter.ai Integration'}
              </CardTitle>
              <CardDescription>
                {status.configured
                  ? 'Manage your Otter transcript ingestion settings'
                  : 'Add your team\'s email domain to get started'
                }
              </CardDescription>
            </div>
            {status.configured && (
              <Badge
                variant="outline"
                className={enabled
                  ? 'text-green-600 border-green-300 bg-green-50'
                  : 'text-muted-foreground border-border'
                }
              >
                {enabled ? (
                  <><Check className="h-3 w-3 mr-1" /> Active</>
                ) : (
                  'Paused'
                )}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Ingest Email Address */}
          {status.ingestEmail && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Ingest Email Address
              </label>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-muted/50 rounded-md border text-sm font-mono">
                  {status.ingestEmail}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyEmail}
                  className="shrink-0"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Email Forwarding Instructions */}
          {status.ingestEmail && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">Set up email forwarding (one-time):</p>
              <p className="text-xs text-muted-foreground">
                Otter.ai sends meeting summary emails after each recorded meeting. Set up a
                forwarding rule to automatically send these to the ingest address above.
              </p>

              <div className="space-y-3">
                {/* Gmail Instructions */}
                <div className="bg-background/50 rounded-md p-3 border border-border/50">
                  <p className="text-xs font-medium mb-1.5">Gmail:</p>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                    <li>Go to <strong>Settings</strong> (gear icon) → <strong>See all settings</strong></li>
                    <li>Click the <strong>Forwarding and POP/IMAP</strong> tab → <strong>Add a forwarding address</strong></li>
                    <li>Enter the ingest email above, click <strong>Next</strong> → <strong>Proceed</strong> → <strong>OK</strong></li>
                    <li>Check the ingest inbox for a verification email from Gmail and <strong>click the confirmation link</strong></li>
                    <li>Back in Gmail Settings, click <strong>Filters and Blocked Addresses</strong> → <strong>Create a new filter</strong></li>
                    <li>In the <strong>From</strong> field, enter: <code className="bg-muted px-1 py-0.5 rounded text-[10px]">no-reply@otter.ai</code></li>
                    <li>Click <strong>Create filter</strong> → check <strong>Forward it to</strong> → select the ingest email from the dropdown</li>
                    <li>Click <strong>Create filter</strong> to save</li>
                  </ol>
                </div>

                {/* Outlook Instructions */}
                <div className="bg-background/50 rounded-md p-3 border border-border/50">
                  <p className="text-xs font-medium mb-1.5">Outlook (Web / New Outlook):</p>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                    <li>Go to <strong>Settings</strong> → <strong>Mail</strong> → <strong>Rules</strong></li>
                    <li>Click <strong>Add new rule</strong></li>
                    <li>Name it "Otter to ReturnCatalyst"</li>
                    <li>Condition: Select <strong>From</strong> and enter <code className="bg-muted px-1 py-0.5 rounded text-[10px]">no-reply@otter.ai</code></li>
                    <li>Action: <strong>Forward to</strong> → enter the ingest email above</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2">
                    Note: Microsoft 365 corporate accounts may block external forwarding by default.
                    If the rule doesn't work, ask your IT admin to enable external forwarding.
                    Also add your email domain to the "Allowed Sender Domains" below — Outlook may
                    change the sender to your address when forwarding.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Allowed Sender Domains */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">
              Allowed Sender Domains
            </label>
            <p className="text-xs text-muted-foreground">
              Only emails from these domains (plus otter.ai) will be accepted.
              Add your team's email domain(s).
            </p>

            {/* Domain List */}
            <div className="space-y-2">
              {domains.map((domain) => (
                <div key={domain} className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md">
                  <span className="text-sm flex-1 font-mono">{domain}</span>
                  <button
                    onClick={() => handleRemoveDomain(domain)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Domain */}
            <div className="flex gap-2">
              <Input
                placeholder="e.g., corbelcap.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddDomain}
                disabled={!newDomain.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {domains.length === 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Add at least one sender domain to start receiving transcripts.
                </p>
              </div>
            )}
          </div>

          {/* Enable/Disable Toggle */}
          {status.configured && (
            <div className="flex items-center justify-between py-3 border-t border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Enable Ingestion</p>
                <p className="text-xs text-muted-foreground">Accept incoming Otter transcripts</p>
              </div>
              <button
                onClick={() => setEnabled(!enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Stats */}
          {status.configured && (
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold tabular-nums">{status.totalIngested || 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Total Imported</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <p className="text-2xl font-bold tabular-nums">{status.recentCount || 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Last 7 Days</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg text-center">
                <p className={`text-2xl font-bold tabular-nums ${unassignedCount > 0 ? 'text-amber-600' : ''}`}>
                  {unassignedCount}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Unassigned</p>
              </div>
            </div>
          )}

          {/* Last Ingested */}
          {status.lastIngestedAt && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              Last transcript received: {status.lastIngestedAt.toLocaleString()}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving || domains.length === 0}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {status.configured ? 'Save Changes' : 'Enable Integration'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
